import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useRazorpayPayment } from "../student-details/fee-details/hooks/useRazorpayPayment";
import { PaymentDetailsResponse } from "./types/PaymentDetailsResponse";
import { useGetInvoicesByStudentIdQuery } from "../student-details/fee-details/api/invoiceApi";
import { Collapse } from "react-bootstrap";

const Billing = () => {
  const routes = all_routes;
  const { id: studentID } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Use the payment hook
  const {
    paymentDetails: payments,
    isLoadingPayment,
    GetStudentPaymentDetails,
  } = useRazorpayPayment();

  const [openSection, setOpenSection] = useState<Record<string, boolean>>({
    payments: false,
    invoices: false,
  });

  // Fetch payments when ID changes
  useEffect(() => {
    let isMounted = true;

    const fetchPayments = async () => {
      if (studentID) {
        await GetStudentPaymentDetails(studentID);
      }
    };

    fetchPayments();

    return () => {
      isMounted = false;
    };
  }, [studentID]);

  // Fetch invoices
  const { data: invoicesData, isLoading: isLoadingInvoices } =
    useGetInvoicesByStudentIdQuery(studentID || "");

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format time
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get payment status badge class
  const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "captured":
      case "authorized":
        return "badge-soft-success";
      case "pending":
      case "created":
        return "badge-soft-warning";
      case "failed":
      case "refunded":
        return "badge-soft-danger";
      default:
        return "badge-soft-secondary";
    }
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method: string) => {
    switch (method?.toLowerCase()) {
      case "upi":
        return "ti ti-qrcode";
      case "card":
        return "fas fa-credit-card";
      case "netbanking":
        return "fas fa-building-columns";
      case "wallet":
        return "fas fa-wallet";
      case "cash":
        return "fas fa-money-bill";
      default:
        return "fas fa-money-check";
    }
  };

  // Group payments by month
  const groupedPayments = React.useMemo(() => {
    const paymentArray = Array.isArray(payments) ? payments : [];
    if (!paymentArray.length) return {};

    return paymentArray.reduce((groups, payment: PaymentDetailsResponse) => {
      const date = new Date(payment.payment_date);
      const monthYear = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });

      if (!groups[monthYear]) {
        groups[monthYear] = [];
      }

      groups[monthYear].push(payment);
      return groups;
    }, {} as Record<string, PaymentDetailsResponse[]>);
  }, [payments]);

  // Group invoices by month
  const groupedInvoices = React.useMemo(() => {
    const invoices = invoicesData?.data?.invoices || [];
    if (!invoices.length) return {};

    return invoices.reduce((groups: Record<string, any[]>, invoice: any) => {
      const date = new Date(invoice.created_at || invoice.due_date);
      const monthYear = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });

      if (!groups[monthYear]) {
        groups[monthYear] = [];
      }

      groups[monthYear].push(invoice);
      return groups;
    }, {});
  }, [invoicesData?.data?.invoices]);

  // Calculate financial summaries
  const financialSummary = React.useMemo(() => {
    const invoices = invoicesData?.data?.invoices || [];
    const paymentArray = Array.isArray(payments) ? payments : [];

    const totalPaymentAmount = paymentArray.reduce(
      (sum: number, p: PaymentDetailsResponse) => sum + Number(p.amount),
      0
    );

    const totalInvoiceOriginalAmount = invoices.reduce(
      (sum: number, inv: any) => sum + Number(inv.amount || 0),
      0
    );

    const totalInvoicePayableAmount = invoices.reduce(
      (sum: number, inv: any) => sum + Number(inv.payable || 0),
      0
    );

    const totalDiscountAmount = invoices.reduce(
      (sum: number, inv: any) =>
        sum + (Number(inv.amount) - Number(inv.payable)),
      0
    );

    const totalPaidFromInvoices = invoices.reduce((sum: number, inv: any) => {
      const invoicePaid =
        inv.student_payments?.reduce(
          (paymentSum: number, payment: any) =>
            payment.payment_status === "completed"
              ? paymentSum + Number(payment.amount)
              : paymentSum,
          0
        ) || 0;
      return sum + invoicePaid;
    }, 0);

    const totalPendingFromInvoices = Math.max(
      0,
      totalInvoicePayableAmount - totalPaidFromInvoices
    );

    return {
      totalPaymentAmount,
      totalInvoiceOriginalAmount,
      totalInvoicePayableAmount,
      totalDiscountAmount,
      totalPaidFromInvoices,
      totalPendingFromInvoices,
    };
  }, [payments, invoicesData?.data?.invoices]);

  // Calculate total payments for all invoices
  const totalPaymentAmount = financialSummary.totalPaymentAmount;

  // Calculate total invoices
  const totalInvoices = invoicesData?.data?.invoices?.length || 0;

  // Calculate total invoices completed
  const totalInvoicesCompleted =
    invoicesData?.data?.invoices?.filter(
      (i: any) => i.invoice_payment_status === "completed"
    )?.length || 0;

  // Calculate total invoices pending
  const totalInvoicesPending = totalInvoices - totalInvoicesCompleted;

  // Calculate total original amount
  const totalInvoiceOriginalAmount =
    financialSummary.totalInvoiceOriginalAmount;

  // Calculate total payable amount
  const totalInvoicePayableAmount = financialSummary.totalInvoicePayableAmount;

  // Calculate total discount amount
  const totalDiscountAmount = financialSummary.totalDiscountAmount;

  // Calculate total paid from invoices
  const totalPaidFromInvoices = financialSummary.totalPaidFromInvoices;

  // Calculate total pending from invoices
  const totalPendingFromInvoices = financialSummary.totalPendingFromInvoices;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {studentID ? "Student Financial Overview" : "All Payments"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.studentList}>Students</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Billing Section
                  </li>
                </ol>
              </nav>
            </div>
            <div>
              <button
                onClick={() => navigate(routes.studentList)}
                className="btn btn-outline-primary me-2"
              >
                <i className="ti ti-arrow-left me-2" />
                Back
              </button>
              <Link
                to={routes.studentFees.replace(":id", studentID || "")}
                className="btn btn-primary"
              >
                <i className="ti ti-file-invoice me-1"></i> View Fees
              </Link>
            </div>
          </div>

          {/* Financial Overview - Row 1 */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card bg-light-primary h-100">
                <div className="card-body text-center">
                  <i className="ti ti-file-invoice fs-3 mb-2"></i>
                  <h5>Total Invoices</h5>
                  <h3 className="mb-0">{totalInvoices}</h3>
                  <small className="text-muted">
                    {totalInvoicesCompleted} completed
                  </small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light-warning h-100">
                <div className="card-body text-center">
                  <i className="ti ti-receipt-2 fs-3 mb-2"></i>
                  <h5>Original Amount</h5>
                  <h3 className="mb-0">
                    ₹{totalInvoiceOriginalAmount.toLocaleString()}
                  </h3>
                  <small className="text-muted">Before discounts</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light-danger h-100">
                <div className="card-body text-center">
                  <i className="ti ti-discount-2 fs-3 mb-2"></i>
                  <h5>Total Discount</h5>
                  <h3 className="mb-0">
                    ₹{totalDiscountAmount.toLocaleString()}
                  </h3>
                  <small className="text-muted">
                    {
                      invoicesData?.data?.invoices?.filter(
                        (i: any) => Number(i.discount_value) > 0
                      ).length
                    }{" "}
                    invoices with discount
                  </small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light-info h-100">
                <div className="card-body text-center">
                  <i className="ti ti-cash-banknote fs-3 mb-2"></i>
                  <h5>Payable Amount</h5>
                  <h3 className="mb-0">
                    ₹{totalInvoicePayableAmount.toLocaleString()}
                  </h3>
                  <small className="text-muted">After discounts</small>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Overview - Row 2 */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card bg-light-success h-100">
                <div className="card-body text-center">
                  <i className="ti ti-check fs-3 mb-2"></i>
                  <h5>Total Paid</h5>
                  <h3 className="mb-0">
                    ₹{totalPaidFromInvoices.toLocaleString()}
                  </h3>
                  <small className="text-muted">
                    {invoicesData?.data?.invoices?.reduce(
                      (count: number, inv: any) =>
                        count + (inv.student_payments?.length || 0),
                      0
                    )}{" "}
                    payments
                  </small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light-danger h-100">
                <div className="card-body text-center">
                  <i className="ti ti-alert-circle fs-3 mb-2"></i>
                  <h5>Pending Amount</h5>
                  <h3 className="mb-0">
                    ₹{totalPendingFromInvoices.toLocaleString()}
                  </h3>
                  <small className="text-muted">
                    {totalInvoicesPending} invoices pending
                  </small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light-info h-100">
                <div className="card-body text-center">
                  <i className="ti ti-calendar-stats fs-3 mb-2"></i>
                  <h5>Active Levels</h5>
                  <h3 className="mb-0">
                    {
                      new Set(
                        invoicesData?.data?.invoices?.map(
                          (i: any) => i.stage_id
                        )
                      ).size
                    }
                  </h3>
                  <small className="text-muted">Course payment levels</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-light-primary h-100">
                <div className="card-body text-center">
                  <i className="ti ti-chart-pie fs-3 mb-2"></i>
                  <h5>Payment Progress</h5>
                  <h3 className="mb-0">
                    {totalInvoicePayableAmount > 0
                      ? Math.round(
                        (totalPaidFromInvoices / totalInvoicePayableAmount) *
                        100
                      )
                      : 0}
                    %
                  </h3>
                  <div className="progress mt-2" style={{ height: "6px" }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{
                        width: `${totalInvoicePayableAmount > 0
                          ? Math.round(
                            (totalPaidFromInvoices /
                              totalInvoicePayableAmount) *
                            100
                          )
                          : 0
                          }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Accordion Sections */}
          <div className="accordion" id="billingAccordion">
            {/* Payments Section */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button
                  className={`accordion-button ${!openSection.payments ? "collapsed" : ""
                    }`}
                  type="button"
                  onClick={() =>
                    setOpenSection((prev) => ({
                      ...prev,
                      payments: !prev.payments,
                    }))
                  }
                  aria-expanded={openSection.payments}
                >
                  <i className="ti ti-cash me-2"></i>
                  Payment History (
                  {Array.isArray(payments) ? payments.length : 0})
                  <span
                    className="ms-auto badge bg-soft-primary rounded-pill me-2"
                    title="Total Amount Received"
                  >
                    ₹{financialSummary.totalPaymentAmount.toLocaleString()}
                  </span>
                </button>
              </h2>
              <Collapse in={openSection.payments}>
                <div className="accordion-body p-0">
                  <div className="card mb-0 border-0">
                    <div className="card-body">
                      {isLoadingPayment ? (
                        <div className="text-center p-5">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Loading payments...
                            </span>
                          </div>
                          <p className="mt-2">
                            Loading payment transactions...
                          </p>
                        </div>
                      ) : !Array.isArray(payments) || payments.length === 0 ? (
                        <div className="text-center p-5">
                          <div className="text-muted">
                            <i className="ti ti-receipt-off fa-3x mb-3"></i>
                            <p>No payment transactions found.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="timeline-activity">
                          {Object.entries(groupedPayments).map(
                            ([monthYear, monthPayments]) => (
                              <div
                                key={monthYear}
                                className="payment-month mb-4"
                              >
                                <h5 className="month-heading position-relative mb-4">
                                  <span className="badge bg-primary px-3 py-2 rounded-pill">
                                    {monthYear}
                                  </span>
                                </h5>

                                {monthPayments.map((payment) => (
                                  <div
                                    key={payment.id}
                                    className={`payment-item card mb-3 border-0 shadow-sm ${getStatusBadgeClass(
                                      payment.status
                                    ).replace(
                                      "badge-soft",
                                      "border-start border-5 border"
                                    )}`}
                                  >
                                    <div className="card-body">
                                      <div className="row">
                                        <div className="col-md-8">
                                          <div className="d-flex mb-2">
                                            <div className="flex-shrink-0">
                                              <div
                                                className={`payment-icon rounded-circle d-flex align-items-center justify-content-center ${getStatusBadgeClass(
                                                  payment.status
                                                )}`}
                                                style={{
                                                  width: "50px",
                                                  height: "50px",
                                                }}
                                              >
                                                <i
                                                  className={`${getPaymentMethodIcon(
                                                    payment.payment_method
                                                  )} fs-4`}
                                                ></i>
                                              </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                              <h5 className="mb-0">
                                                {payment.transaction_details
                                                  ?.notes?.student_name ||
                                                  "Student"}{" "}
                                                -{" "}
                                                {payment.transaction_details
                                                  ?.notes?.course_name ||
                                                  "Course"}
                                              </h5>
                                              <p className="text-muted mb-1">
                                                <i className="ti ti-calendar-event me-1"></i>
                                                {formatDate(
                                                  payment.payment_date
                                                )}{" "}
                                                at{" "}
                                                {formatTime(
                                                  payment.payment_date
                                                )}
                                              </p>
                                              <div className="d-flex flex-wrap gap-2 mt-2">
                                                <span
                                                  className={`badge ${getStatusBadgeClass(
                                                    payment.status
                                                  )}`}
                                                >
                                                  <i className="ti ti-circle-filled fs-5 me-1"></i>
                                                  {payment.status}
                                                </span>
                                                <span className="badge badge-soft-info">
                                                  <i className="ti ti-book me-1"></i>
                                                  Level:{" "}
                                                  {payment.transaction_details
                                                    ?.notes?.stage_name ||
                                                    "N/A"}
                                                </span>
                                                <span className="badge badge-soft-secondary">
                                                  <i
                                                    className={`${getPaymentMethodIcon(
                                                      payment.payment_method
                                                    )} me-1`}
                                                  ></i>
                                                  {payment.payment_method ||
                                                    "N/A"}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-md-4 d-flex flex-column justify-content-center align-items-end">
                                          <h4 className="mb-2 text-primary">
                                            ₹
                                            {Number(
                                              payment.amount
                                            ).toLocaleString()}
                                          </h4>
                                        </div>
                                      </div>
                                      <div className="mt-3 pt-3 border-top">
                                        <div className="row g-3">
                                          <div className="col-md-3">
                                            <small className="text-muted d-block">
                                              Payment ID
                                            </small>
                                            <span
                                              className="text-truncate d-inline-block"
                                              style={{ maxWidth: "100%" }}
                                            >
                                              {payment.payment_id}
                                            </span>
                                          </div>
                                          <div className="col-md-3">
                                            <small className="text-muted d-block">
                                              Order ID
                                            </small>
                                            <span
                                              className="text-truncate d-inline-block"
                                              style={{ maxWidth: "100%" }}
                                            >
                                              {payment.order_id}
                                            </span>
                                          </div>
                                          <div className="col-md-3">
                                            <small className="text-muted d-block">
                                              Reference
                                            </small>
                                            <span
                                              className="text-truncate d-inline-block"
                                              style={{ maxWidth: "100%" }}
                                            >
                                              {payment.transaction_details
                                                ?.acquirer_data
                                                ?.upi_transaction_id ||
                                                payment.transaction_details
                                                  ?.acquirer_data
                                                  ?.bank_transaction_id ||
                                                payment.transaction_details
                                                  ?.acquirer_data?.rrn ||
                                                "N/A"}
                                            </span>
                                          </div>
                                          <div className="col-md-3">
                                            <small className="text-muted d-block">
                                              Bank/UPI
                                            </small>
                                            <span>
                                              {payment.transaction_details
                                                ?.bank ||
                                                payment.transaction_details?.upi
                                                  ?.vpa ||
                                                "N/A"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>

            {/* Invoices Section */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button
                  className={`accordion-button ${!openSection.invoices ? "collapsed" : ""
                    }`}
                  type="button"
                  onClick={() =>
                    setOpenSection((prev) => ({
                      ...prev,
                      invoices: !prev.invoices,
                    }))
                  }
                  aria-expanded={openSection.invoices}
                >
                  <i className="ti ti-file-invoice me-2"></i>
                  Invoice History ({totalInvoices})
                  <div className="ms-auto d-flex align-items-center gap-2">
                    <span
                      className="badge bg-soft-danger rounded-pill me-1"
                      title="Total Discounts Applied"
                    >
                      <i className="ti ti-discount-2 me-1"></i>₹
                      {totalDiscountAmount.toLocaleString()}
                    </span>
                    <span
                      className="badge bg-soft-warning rounded-pill me-1"
                      title="Total Payable Amount"
                    >
                      ₹{totalInvoicePayableAmount.toLocaleString()}
                    </span>
                  </div>
                </button>
              </h2>
              <Collapse in={openSection.invoices}>
                <div className="accordion-body p-0">
                  <div className="card mb-0 border-0">
                    <div className="card-body">
                      {isLoadingInvoices ? (
                        <div className="text-center p-5">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">
                              Loading invoices...
                            </span>
                          </div>
                          <p className="mt-2">Loading invoice data...</p>
                        </div>
                      ) : invoicesData?.data?.invoices?.length === 0 ? (
                        <div className="text-center p-5">
                          <div className="text-muted">
                            <i className="ti ti-file-off fa-3x mb-3"></i>
                            <p>No invoices found for this student.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="timeline-activity">
                          {Object.entries(groupedInvoices).map(
                            ([monthYear, monthInvoices]) => (
                              <div
                                key={monthYear}
                                className="invoice-month mb-4"
                              >
                                <h5 className="month-heading position-relative mb-4">
                                  <span className="badge bg-warning px-3 py-2 rounded-pill">
                                    {monthYear}
                                  </span>
                                </h5>

                                {monthInvoices.map((invoice: any) => {
                                  const statusClass =
                                    invoice.invoice_payment_status ===
                                      "completed"
                                      ? "success"
                                      : invoice.invoice_payment_status ===
                                        "partial"
                                        ? "info"
                                        : "warning";

                                  // Calculate total paid amount from invoice payments
                                  const totalPaid =
                                    invoice.student_payments?.reduce(
                                      (sum: number, payment: any) =>
                                        payment.payment_status === "completed"
                                          ? sum + Number(payment.amount)
                                          : sum,
                                      0
                                    ) || 0;

                                  // Calculate remaining amount
                                  const remainingAmount = Math.max(
                                    0,
                                    Number(invoice.payable) - totalPaid
                                  );

                                  // Format discount information
                                  const discountInfo = invoice.discount_type
                                    ? `${invoice.discount_type === "fixed"
                                      ? "₹"
                                      : ""
                                    }${invoice.discount_value}${invoice.discount_type === "percent"
                                      ? "%"
                                      : ""
                                    }`
                                    : "None";

                                  return (
                                    <div
                                      key={invoice.id}
                                      className={`invoice-item card mb-3 border-0 shadow-sm border-start border-5 border-${statusClass}`}
                                    >
                                      <div className="card-body">
                                        <div className="row">
                                          <div className="col-md-8">
                                            <div className="d-flex mb-2">
                                              <div className="flex-shrink-0">
                                                <div
                                                  className={`invoice-icon rounded-circle d-flex align-items-center justify-content-center badge-soft-${statusClass}`}
                                                  style={{
                                                    width: "50px",
                                                    height: "50px",
                                                  }}
                                                >
                                                  <i className="ti ti-file-invoice fs-4"></i>
                                                </div>
                                              </div>
                                              <div className="flex-grow-1 ms-3">
                                                <h5 className="mb-0">
                                                  {invoice.type ===
                                                    "registrationfee"
                                                    ? "Registration Fee"
                                                    : `${invoice.stage?.name ||
                                                    "Stage"
                                                    } - ${invoice.course?.name ||
                                                    "Course"
                                                    }`}
                                                </h5>
                                                <p className="text-muted mb-1">
                                                  <i className="ti ti-calendar-due me-1"></i>
                                                  Due Date:{" "}
                                                  {formatDate(invoice.due_date)}
                                                </p>
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                  <span
                                                    className={`badge badge-soft-${statusClass}`}
                                                  >
                                                    <i className="ti ti-circle-filled fs-5 me-1"></i>
                                                    {invoice.invoice_payment_status ===
                                                      "completed"
                                                      ? "Paid"
                                                      : invoice.invoice_payment_status ===
                                                        "partial"
                                                        ? "Partially Paid"
                                                        : "Pending"}
                                                  </span>
                                                  <span className="badge badge-soft-primary">
                                                    <i className="ti ti-category me-1"></i>
                                                    {invoice.type ===
                                                      "registrationfee"
                                                      ? "Registration Fee"
                                                      : "Course Fee"}
                                                  </span>
                                                  {invoice.student_payments
                                                    ?.length > 0 && (
                                                      <span className="badge badge-soft-info">
                                                        <i className="ti ti-cash me-1"></i>
                                                        {
                                                          invoice.student_payments
                                                            .length
                                                        }{" "}
                                                        Payment
                                                        {invoice.student_payments
                                                          .length > 1
                                                          ? "s"
                                                          : ""}
                                                      </span>
                                                    )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-md-4 d-flex flex-column justify-content-center align-items-end">
                                            <div className="text-end">
                                              <div className="mb-1">
                                                <span className="text-muted me-2">
                                                  Original:
                                                </span>
                                                <span>
                                                  ₹
                                                  {Number(
                                                    invoice.amount
                                                  ).toLocaleString()}
                                                </span>
                                              </div>
                                              {Number(invoice.discount_value) >
                                                0 && (
                                                  <div className="mb-1">
                                                    <span className="text-danger me-2">
                                                      Discount:
                                                    </span>
                                                    <span className="text-danger">
                                                      -₹
                                                      {(
                                                        Number(invoice.amount) -
                                                        Number(invoice.payable)
                                                      ).toLocaleString()}{" "}
                                                      ({discountInfo})
                                                    </span>
                                                  </div>
                                                )}
                                              <h4 className="mb-0 text-primary">
                                                ₹
                                                {Number(
                                                  invoice.payable
                                                ).toLocaleString()}
                                              </h4>
                                            </div>
                                            <div className="mt-2">
                                              <Link
                                                to={routes.studentFeesInvoice
                                                  .replace(
                                                    ":id",
                                                    studentID || ""
                                                  )
                                                  .replace(
                                                    ":invoiceId",
                                                    invoice.id.toString()
                                                  )}
                                                className="btn btn-sm btn-outline-primary"
                                              >
                                                <i className="ti ti-file-invoice me-1"></i>
                                                View Invoice
                                              </Link>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="mt-3 pt-3 border-top">
                                          <div className="row g-3">
                                            <div className="col-md-4">
                                              <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                  <small className="text-muted d-block">
                                                    Paid Amount
                                                  </small>
                                                  <span className="text-success">
                                                    ₹
                                                    {totalPaid.toLocaleString()}
                                                  </span>
                                                </div>
                                                <div className="flex-shrink-0 ms-3">
                                                  <div
                                                    className="progress"
                                                    style={{
                                                      width: "60px",
                                                      height: "6px",
                                                    }}
                                                  >
                                                    <div
                                                      className="progress-bar bg-success"
                                                      role="progressbar"
                                                      style={{
                                                        width: `${Math.min(
                                                          100,
                                                          (totalPaid /
                                                            Number(
                                                              invoice.payable
                                                            )) *
                                                          100
                                                        )}%`,
                                                      }}
                                                    ></div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="col-md-4">
                                              <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                  <small className="text-muted d-block">
                                                    Remaining
                                                  </small>
                                                  <span className="text-danger">
                                                    ₹
                                                    {remainingAmount.toLocaleString()}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="col-md-4">
                                              <small className="text-muted d-block">
                                                Reference
                                              </small>
                                              <span className="badge badge-soft-dark">
                                                INV-{invoice.id}
                                              </span>
                                              {invoice.type ===
                                                "registrationfee" && (
                                                  <span className="badge badge-soft-primary ms-1">
                                                    Registration
                                                  </span>
                                                )}
                                            </div>
                                          </div>

                                          {/* Show payment methods used if there are payments */}
                                          {invoice.student_payments &&
                                            invoice.student_payments.length >
                                            0 && (
                                              <div className="mt-3">
                                                <small className="text-muted d-block mb-2">
                                                  Payment History
                                                </small>
                                                <div className="d-flex flex-wrap gap-2">
                                                  {invoice.student_payments.map(
                                                    (
                                                      payment: any,
                                                      idx: number
                                                    ) => (
                                                      <div
                                                        key={`payment-${payment.id}-${idx}`}
                                                        className="badge badge-soft-secondary p-2"
                                                      >
                                                        <i
                                                          className={`${getPaymentMethodIcon(
                                                            payment.payment_method
                                                          )} me-1`}
                                                        ></i>
                                                        ₹
                                                        {Number(
                                                          payment.amount
                                                        ).toLocaleString()}{" "}
                                                        •{" "}
                                                        {new Date(
                                                          payment.payment_date
                                                        ).toLocaleDateString()}
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Billing;
